import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { api, getInviteCode } from "../lib/api";
import type { PipelineEvent, PipelineStep as PipelineStepType } from "../lib/types";
import Layout from "../components/Layout";
import PipelineStepRow from "../components/PipelineStep";

const LEGACY_STEP_NAMES = [
  "Business Case",
  "Product Specification",
  "Technical Architecture",
  "Epic Plan",
];

export default function Pipeline() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const runId = searchParams.get("run_id");
  const projectIdParam = searchParams.get("project");

  const [steps, setSteps] = useState<PipelineStepType[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Step timing
  const stepStartTimes = useRef<Record<number, number>>({});
  const [stepElapsed, setStepElapsed] = useState<Record<number, string>>({});

  // Auth guard
  useEffect(() => {
    if (!getInviteCode()) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Redirect if no run_id
  useEffect(() => {
    if (!runId) navigate("/dashboard", { replace: true });
  }, [runId, navigate]);

  // Fallback: if no pipeline_info within 5s, use legacy 4-step names
  useEffect(() => {
    if (initialized) return;
    const timer = setTimeout(() => {
      if (!initialized) {
        setSteps(LEGACY_STEP_NAMES.map((name) => ({ name, status: "pending" })));
        setInitialized(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [initialized]);

  // Handle individual SSE event from backend
  const handleEvent = useCallback((evt: PipelineEvent) => {
    const stepIdx = evt.step !== undefined ? evt.step : -1;

    switch (evt.event) {
      case "pipeline_info":
        if (evt.steps) {
          setSteps(evt.steps.map((name: string) => ({ name, status: "pending" })));
          setInitialized(true);
        }
        break;

      case "step_start":
        if (stepIdx >= 0) {
          stepStartTimes.current[stepIdx] = Date.now();
          setSteps((prev) =>
            prev.map((s, i) =>
              i === stepIdx ? { ...s, status: "running" } : s
            )
          );
        }
        break;

      case "step_complete":
        if (stepIdx >= 0) {
          if (stepStartTimes.current[stepIdx]) {
            const elapsed = Date.now() - stepStartTimes.current[stepIdx];
            setStepElapsed((prev) => ({
              ...prev,
              [stepIdx]: formatElapsed(elapsed),
            }));
          }
          setSteps((prev) =>
            prev.map((s, i) =>
              i === stepIdx ? { ...s, status: "completed" } : s
            )
          );
        }
        break;

      case "error":
        if (stepIdx >= 0) {
          setSteps((prev) =>
            prev.map((s, i) =>
              i === stepIdx ? { ...s, status: "failed" } : s
            )
          );
        }
        setErrorMsg(evt.detail ?? null);
        break;

      case "done":
        if (evt.detail === "completed") {
          setDone(true);
        } else {
          setFailed(true);
        }
        break;
    }
  }, []);

  // Elapsed time ticker for running steps
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setSteps((currentSteps) => {
        let changed = false;
        currentSteps.forEach((step, i) => {
          if (step.status === "running" && stepStartTimes.current[i]) {
            const elapsed = formatElapsed(now - stepStartTimes.current[i]);
            if (elapsed !== stepElapsed[i]) {
              changed = true;
            }
          }
        });
        // Only trigger re-render if something actually changed
        if (changed) {
          const updated: Record<number, string> = {};
          currentSteps.forEach((step, i) => {
            if (step.status === "running" && stepStartTimes.current[i]) {
              updated[i] = formatElapsed(now - stepStartTimes.current[i]);
            } else if (stepElapsed[i]) {
              updated[i] = stepElapsed[i];
            }
          });
          setStepElapsed(updated);
        }
        return currentSteps; // don't mutate steps here
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [stepElapsed]);

  // SSE connection — listen on named events
  useEffect(() => {
    if (!runId) return;

    const es = api.pipelineStatus(runId);

    function onEvent(event: MessageEvent) {
      try {
        const parsed: PipelineEvent = JSON.parse(event.data);
        handleEvent(parsed);

        if (parsed.event === "done") {
          es.close();
        }
      } catch {
        // Non-JSON — ignore
      }
    }

    // Listen to each named event type the backend sends
    es.addEventListener("pipeline_info", onEvent);
    es.addEventListener("step_start", onEvent);
    es.addEventListener("step_complete", onEvent);
    es.addEventListener("error", onEvent);
    es.addEventListener("done", onEvent);
    // Fallback for unnamed messages
    es.onmessage = onEvent;

    es.onerror = () => {
      Sentry.captureMessage("SSE connection error", {
        level: "error",
        extra: {
          readyState: es.readyState,
          runId,
          projectId: projectIdParam,
        },
      });
      es.close();
    };

    return () => es.close();
  }, [runId, handleEvent]);

  const completedCount = steps.filter((s) => s.status === "completed").length;
  const progressPct = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  return (
    <Layout showHeader>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          <h2 className="text-xl sm:text-2xl font-semibold text-white text-center mb-8">
            {done
              ? "Your product plan is ready!"
              : failed
              ? "Pipeline failed"
              : "Building your product plan..."}
          </h2>

          {/* Steps */}
          <div className="bg-slate-800/40 rounded-2xl p-5 mb-6">
            {!initialized && !done && !failed && (
              <div className="text-center py-4 text-slate-500 text-sm">
                Initializing pipeline...
              </div>
            )}
            {steps.map((step, i) => (
              <PipelineStepRow
                key={i}
                name={step.name}
                status={step.status}
                elapsed={stepElapsed[i] ?? null}
                error={step.status === "failed" ? errorMsg : null}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span>Progress</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Time estimate */}
          {!done && !failed && (
            <p className="text-center text-sm text-slate-500">
              Each step takes 2-5 minutes. Total estimated time: ~12-20 minutes.
            </p>
          )}

          {/* Actions */}
          {done && (
            <div className="text-center mt-6">
              {steps.length === 1 && steps[0].name === "Business Case" && projectIdParam ? (
                <button
                  onClick={() => navigate(`/chat?project=${projectIdParam}`)}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-6 py-3 text-base transition-colors"
                >
                  Review Business Case
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/artifacts?run_id=${runId}`)}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-6 py-3 text-base transition-colors"
                >
                  View Artifacts
                </button>
              )}
            </div>
          )}

          {failed && (
            <div className="text-center mt-6 space-y-3">
              <p className="text-sm text-slate-400">
                Something went wrong.{" "}
                {errorMsg && (
                  <span className="text-red-400">{errorMsg}</span>
                )}
              </p>
              <p className="text-sm text-slate-500">
                Please contact{" "}
                <a
                  href="mailto:filipe@verefy.ai"
                  className="text-blue-400 hover:underline"
                >
                  filipe@verefy.ai
                </a>{" "}
                for help.
              </p>
              <button
                onClick={() => navigate("/dashboard")}
                className="border border-slate-600 hover:border-slate-500 text-slate-300 rounded-xl px-5 py-2.5 text-sm transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
